import React, { useEffect, useMemo, useState } from 'react';
import { Grid, Button, Popover, Box, Typography } from '@mui/material';
import { Icon24UtilityChevron, Icon24InfoLocation } from '@care/react-icons';
import { InlineTextField, useActiveTheme } from '@care/react-component-lib';
import { Icon24Location, Icon24ChevronSmall } from '@care/react-embrace-icons';
import { AmplitudeEvent, trackAmplitudeEvent } from '@/lib/analyticsHelper';
import { SEOSegmentationFilterInput } from '@/__generated__/globalTypes';
import { loadFilters } from '@/utils/filtersStorage';
import { getZipcodeFromGeoInfo } from '@/utils/getZipcodeFromGeoInfo';
import { usePageAttributes } from '../contexts/PageAttributesContext';
import useZipCodeValidation from '../hooks/useZipCodeValidation';
import { useSegmentationFiltersContext } from '../contexts/SegmentationFiltersContext';

const LocationFilter = () => {
  const { isEmbraceTheme, isCareTheme } = useActiveTheme();
  const { filters, setFilters, fetchResults } = useSegmentationFiltersContext();
  const { userGeoInfo } = usePageAttributes();
  const { validate, hasError } = useZipCodeValidation();
  const localStorageFilters = loadFilters();

  const [anchorEl, setAnchorEl] = useState<(EventTarget & Element) | null>(null);
  const [zipCodeInput, setZipCodeInput] = useState<string>('');
  const [cityInput, setCityInput] = useState<string>('');
  const [stateInput, setStateInput] = useState<string>('');

  useEffect(() => {
    const fetchGeoInfo = async () => {
      const initialZipCode = getZipcodeFromGeoInfo(userGeoInfo);
      setZipCodeInput(initialZipCode);

      const data = await validate(initialZipCode);
      setCityInput(data?.city ?? '');
      setStateInput(data?.state ?? '');
    };
    fetchGeoInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (value.length > 5) return;
    setZipCodeInput(value);

    if (value.length <= 5) {
      const data = await validate(value);
      setCityInput(data?.city ?? '');
      setStateInput(data?.state ?? '');
    }
  };

  const handleClick = (event: React.MouseEvent) => {
    setAnchorEl(event.currentTarget);
    trackAmplitudeEvent(AmplitudeEvent.clickOnSegmentationFilter, {
      zipCode: zipCodeInput,
    });
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleApplyClick = () => {
    const resetPage = zipCodeInput === '' || zipCodeInput !== filters?.location?.zipcode;
    let updatedFilters: SEOSegmentationFilterInput = { zipcode: zipCodeInput };

    setFilters({
      location: {
        zipcode: zipCodeInput,
        city: cityInput,
        state: stateInput,
      },
    });

    if (localStorageFilters) {
      updatedFilters = {
        ...localStorageFilters,
        zipcode: zipCodeInput,
      };
    }

    fetchResults(updatedFilters, resetPage);

    setAnchorEl(null);
    trackAmplitudeEvent(AmplitudeEvent.applySegmentationFilter, {
      zipCode: zipCodeInput,
    });
  };

  const handleKeyUp = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleApplyClick();
    }
  };

  const getHelperText = () => {
    if (hasError) return 'Enter a valid ZIP code (e.g. “02453”)';

    if (zipCodeInput && cityInput && stateInput) {
      return `${cityInput}, ${stateInput}`;
    }

    return '';
  };

  const getDisplayText = () => {
    if (zipCodeInput && cityInput && stateInput) {
      return `${cityInput}, ${stateInput}`;
    }

    return 'City, State';
  };

  const open = Boolean(anchorEl);

  const showDisplay = useMemo(() => {
    return Boolean(zipCodeInput && cityInput && stateInput);
  }, [zipCodeInput, cityInput, stateInput]);

  return (
    <Box mb={{ xs: 3, sm: 6 }} mr={3}>
      <Button
        aria-describedby="location-filter"
        color={isEmbraceTheme ? 'primary' : 'secondary'}
        variant="outlined"
        onClick={handleClick}
        size={isEmbraceTheme ? 'small' : 'medium'}
        sx={(theme) => ({
          ...(isCareTheme && {
            minWidth: '252px',
            height: '48px',
            borderRadius: 4,
            borderColor: theme.palette.care.grey[300],
          }),
          ...(isEmbraceTheme && {
            minWidth: '252px',
          }),
        })}
        startIcon={isEmbraceTheme ? <Icon24Location /> : <Icon24InfoLocation />}
        endIcon={isEmbraceTheme ? <Icon24ChevronSmall /> : <Icon24UtilityChevron />}>
        <Typography
          variant="body3"
          sx={(theme) => ({
            flex: 1,
            textAlign: 'left',
            fontSize: '1.125rem',
            fontWeight: showDisplay ? 700 : 400,
            color: showDisplay ? undefined : theme.palette.care.grey[500],
          })}>
          {getDisplayText()}
        </Typography>
      </Button>
      <Popover
        id="location-filter"
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        sx={(theme) => ({
          '& .MuiPaper-root': {
            minWidth: '100px',
            borderRadius: '24px',
            border: `1px solid ${theme.palette.care.grey[300]}`,
            marginTop: theme.spacing(1),
            boxShadow: '0px 4px 16px rgba(0, 0, 0, 0.15);',
            padding: theme.spacing(3, 2),
            maxWidth: '375px',
            width: '252px',
          },
          '& .MuiLink-root': {
            textAlign: 'right',
          },
        })}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <InlineTextField
              id="locationFilter-zipCode"
              name="locationFilter-zipCode"
              label="ZIP code"
              type="number"
              value={zipCodeInput}
              error={hasError}
              helperText={getHelperText()}
              onKeyUp={handleKeyUp}
              onChange={onChange}
              sx={{
                paddingBottom: '16px',
                // removes number arrows from Chrome, Safari, Edge, Opera
                '& input::-webkit-outer-spin-button, input::-webkit-inner-spin-button': {
                  appearance: 'none',
                  margin: 0,
                },
                // removes number arrows from Firefox
                '& input[type=number]': {
                  appearance: 'textfield',
                },
              }}
            />
          </Grid>
          <Grid item xs={12} textAlign="center">
            <Button
              color="primary"
              variant="contained"
              onClick={handleApplyClick}
              disabled={(zipCodeInput?.length > 0 && zipCodeInput?.length < 5) || hasError}>
              Apply
            </Button>
          </Grid>
        </Grid>
      </Popover>
    </Box>
  );
};

export default LocationFilter;
